import type { JSX, Signal } from '@builder.io/qwik';
import {
  component$,
  useContext,
  useSignal,
  useOnDocument,
  $,
} from '@builder.io/qwik';
import { AppDataContext } from '~/contexts/AppDataContext';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import BodyWrap from '~/components/Layout/Body/BodyWrap';
import { getLazyLoadArticle } from '~/utilities/getLazyLoadArticle';
import { FINITE_SCROLL_OFFSET } from '~/constants/FINITE_SCROLL';
import { runFiniteScrollInit } from '~/utilities/runFiniteScrollInit';

type FiniteScrollType = {
  finiteScrollIndex: number;
  finiteScrollStarts: Signal;
};

export default component$<FiniteScrollType>(
  ({ finiteScrollIndex, finiteScrollStarts }): JSX.Element => {
    const appData = useContext(AppDataContext);
    const nextArticle = useSignal<boolean | ArticleInterface>(false);
    const nextArticleLoading = useSignal<boolean>(false);
    const outputRef = useSignal<Element>();
    const distanceFromTop = useSignal<number>(0);

    /*

    Tried using useVisibleTask$ for this but it didn't work.

    As soon as useVisibleTask$ fires in the first FiniteScroll component,
    all articles loaded (meaning NUM_OF_ARTICLES articles).

    It's as if only the position of the first FiniteScroll component with useVisibleTask$ was used
    to fire the event for all subsequent NextArticle until NUM_OF_ARTICLES is reached.

    But theoretically, we should have to scroll through an entire Article
    to get to the FiniteScroll component and fire useVisibleTask$

    Instead, we're getting the position of the ref element, updating it as more elements load,
    and when we scroll past that point, we load the next article

     */
    useOnDocument(
      'scroll',
      $(async () => {
        if (!outputRef.value) return;

        const rect = outputRef.value.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const newDistanceFromTop = rect.top + scrollTop;
        const scrollWOffset =
          document.documentElement.scrollTop + FINITE_SCROLL_OFFSET;

        // As items load, the distanceFromTop of the ref is going to increase
        // We don't want to capture all value changes,
        // because there seems to be a relocation in the DOM that happens that will decrease distance
        // when we don't actually want the distance decreased
        if (newDistanceFromTop > distanceFromTop.value) {
          distanceFromTop.value = newDistanceFromTop;
        }

        if (
          scrollWOffset > distanceFromTop.value &&
          !nextArticleLoading.value
        ) {
          nextArticleLoading.value = true;
          await getLazyLoadArticle(appData, finiteScrollIndex, nextArticle);

          finiteScrollStarts.value.push({
            distanceFromTop: distanceFromTop.value,
            url:
              typeof nextArticle.value === 'object' &&
              'articleUrl' in nextArticle.value
                ? nextArticle.value.articleUrl
                : '',
          });

          runFiniteScrollInit();
        }
      })
    );

    if (!nextArticle.value) {
      return <div ref={outputRef} />;
    }

    // Once the article loads, recursively load BodyWrap to display the Article and the next FiniteScroll intersection
    return (
      <>
        <div class='mt-10 mb-8 border-t-2 border-solid border-lightGrey' />
        <BodyWrap
          finiteScrollIndex={finiteScrollIndex + 1}
          article={nextArticle.value as ArticleInterface}
          finiteScrollStarts={finiteScrollStarts}
        />
      </>
    );
  }
);
